import React, { forwardRef, useState, useRef, useEffect } from "react";
//font awsome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

const Window = forwardRef(
  (
    {
      id,
      title = "Untitled Window",
      initialPosition = { x: 100, y: 100 },
      initialSize = { width: 600, height: 400 },
      minSize = { width: 300, height: 200 },
      isOpen = false,
      isMinimized = false,
      zIndex = 100,
      onClose,
      onMinimize,
      onRestore,
      onFocus,
      onMove,
      onResize,
      children,
    },
    ref,
  ) => {
    const [position, setPosition] = useState(initialPosition);
    const [size, setSize] = useState(initialSize);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [resizeStart, setResizeStart] = useState(null);
    const [resizeDirection, setResizeDirection] = useState("");
    const [isMaximized, setIsMaximized] = useState(false);
    const [preMaximizeState, setPreMaximizeState] = useState(null);

    const titleBarRef = useRef(null);

    // Handle title bar dragging
    const handleTitleBarMouseDown = (e) => {
      if (e.target.closest(".window-controls") || isMaximized) return;

      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });

      if (onFocus) onFocus(id);
    };

    // Handle window dragging
    const handleMouseMove = (e) => {
      if (isDragging && dragStart) {
        const newPosition = {
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        };

        // Keep window within viewport bounds
        const maxX = window.innerWidth - 100; // Leave some space for visibility
        const maxY = window.innerHeight - 50;

        newPosition.x = Math.max(0, Math.min(newPosition.x, maxX));
        newPosition.y = Math.max(0, Math.min(newPosition.y, maxY));

        setPosition(newPosition);
        if (onMove) onMove(id, newPosition);
      } else if (isResizing && resizeStart) {
        handleResize(e);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setDragStart(null);
      setResizeStart(null);
      setResizeDirection("");
    };

    // Handle window resizing
    const handleResizeMouseDown = (direction, e) => {
      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);
      setResizeDirection(direction);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
        left: position.x,
        top: position.y,
      });

      if (onFocus) onFocus(id);
    };

    const handleResize = (e) => {
      if (!resizeStart || !resizeDirection) return;

      let newSize = { ...size };
      let newPosition = { ...position };

      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      switch (resizeDirection) {
        case "se": // Southeast (bottom-right)
          newSize.width = Math.max(minSize.width, resizeStart.width + deltaX);
          newSize.height = Math.max(
            minSize.height,
            resizeStart.height + deltaY,
          );
          break;
        case "s": // South (bottom)
          newSize.height = Math.max(
            minSize.height,
            resizeStart.height + deltaY,
          );
          break;
        case "e": // East (right)
          newSize.width = Math.max(minSize.width, resizeStart.width + deltaX);
          break;
        case "sw": // Southwest (bottom-left)
          newSize.width = Math.max(minSize.width, resizeStart.width - deltaX);
          newSize.height = Math.max(
            minSize.height,
            resizeStart.height + deltaY,
          );
          if (newSize.width > minSize.width) {
            newPosition.x = resizeStart.left + deltaX;
          }
          break;
        case "w": // West (left)
          newSize.width = Math.max(minSize.width, resizeStart.width - deltaX);
          if (newSize.width > minSize.width) {
            newPosition.x = resizeStart.left + deltaX;
          }
          break;
        case "nw": // Northwest (top-left)
          newSize.width = Math.max(minSize.width, resizeStart.width - deltaX);
          newSize.height = Math.max(
            minSize.height,
            resizeStart.height - deltaY,
          );
          if (newSize.width > minSize.width) {
            newPosition.x = resizeStart.left + deltaX;
          }
          if (newSize.height > minSize.height) {
            newPosition.y = resizeStart.top + deltaY;
          }
          break;
        case "n": // North (top)
          newSize.height = Math.max(
            minSize.height,
            resizeStart.height - deltaY,
          );
          if (newSize.height > minSize.height) {
            newPosition.y = resizeStart.top + deltaY;
          }
          break;
        case "ne": // Northeast (top-right)
          newSize.width = Math.max(minSize.width, resizeStart.width + deltaX);
          newSize.height = Math.max(
            minSize.height,
            resizeStart.height - deltaY,
          );
          if (newSize.height > minSize.height) {
            newPosition.y = resizeStart.top + deltaY;
          }
          break;
      }

      setSize(newSize);
      setPosition(newPosition);
      if (onResize) onResize(id, newSize, newPosition);
    };

    // Handle maximize/restore
    const handleMaximize = () => {
      if (isMaximized) {
        // Restore
        if (preMaximizeState) {
          setPosition(preMaximizeState.position);
          setSize(preMaximizeState.size);
          setPreMaximizeState(null);
        }
        setIsMaximized(false);
        if (onRestore) onRestore(id);
      } else {
        // Maximize
        setPreMaximizeState({ position, size });
        setPosition({ x: 0, y: 0 });
        setSize({ width: window.innerWidth, height: window.innerHeight });
        setIsMaximized(true);
      }
    };

    // Handle minimize
    const handleMinimize = () => {
      if (onMinimize) onMinimize(id);
    };

    // Handle close
    const handleClose = () => {
      if (onClose) onClose(id);
    };

    // Handle double-click on title bar (maximize/restore)
    const handleTitleBarDoubleClick = () => {
      handleMaximize();
    };

    // Add event listeners
    useEffect(() => {
      if (isDragging || isResizing) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
      }
    }, [isDragging, isResizing, dragStart, resizeStart]);

    // Don't render if window is not open
    if (!isOpen) return null;

    // Window styles
    const windowStyle = {
      position: "fixed",
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: `${size.width}px`,
      height: `${size.height}px`,
      backgroundColor: "#ffffff6e",
      border: "1px solid #ccc",
      //borderRadius: "8px 8px 0 0",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
      display: isMinimized ? "none" : "flex",
      flexDirection: "column",
      zIndex: zIndex,
      overflow: "hidden",
      userSelect: isDragging ? "none" : "auto",
    };

    const titleBarStyle = {
      height: "32px",
      backgroundColor: "#010081", //"hsl(218.78deg 100% 44.9%)",
      borderBottom: "1px solid #ccc",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 8px",
      paddingRight: "0px",
      cursor: isDragging ? "grabbing" : "grab",
      userSelect: "none",
      color: "white",
    };

    const contentStyle = {
      flex: 1,
      overflow: "auto",
      backgroundColor: "#ffffffff",
    };

    // Resize handles
    const resizeHandles = [
      {
        direction: "n",
        cursor: "n-resize",
        style: { top: 0, left: "8px", right: "8px", height: "4px" },
      },
      {
        direction: "ne",
        cursor: "ne-resize",
        style: { top: 0, right: 0, width: "8px", height: "8px" },
      },
      {
        direction: "e",
        cursor: "e-resize",
        style: { top: "8px", right: 0, bottom: "8px", width: "4px" },
      },
      {
        direction: "se",
        cursor: "se-resize",
        style: { bottom: 0, right: 0, width: "8px", height: "8px" },
      },
      {
        direction: "s",
        cursor: "s-resize",
        style: { bottom: 0, left: "8px", right: "8px", height: "4px" },
      },
      {
        direction: "sw",
        cursor: "sw-resize",
        style: { bottom: 0, left: 0, width: "8px", height: "8px" },
      },
      {
        direction: "w",
        cursor: "w-resize",
        style: { top: "8px", left: 0, bottom: "8px", width: "4px" },
      },
      {
        direction: "nw",
        cursor: "nw-resize",
        style: { top: 0, left: 0, width: "8px", height: "8px" },
      },
    ];

    return (
      <div ref={ref} style={windowStyle}>
        {/* Title Bar */}
        <div
          className="window-title-bar"
          ref={titleBarRef}
          style={titleBarStyle}
          onMouseDown={handleTitleBarMouseDown}
          onDoubleClick={handleTitleBarDoubleClick}
        >
          <span style={{ fontSize: "14px", fontWeight: "500" }}>{title}</span>
          <div
            className="window-controls"
            style={{
              display: "flex",
              height: "100%",
              gap: "2px",
              alignItems: "right",
            }}
          >
            <button
              onClick={handleMinimize}
              style={{
                width: "25px",
                //   height: "25px",
                height: "100%",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "17px",
                display: "flex",
                alignItems: "end",
                justifyContent: "center",
                color: "white",
              }}
              className="window-control-button"
              title="Minimize"
            >
              —
            </button>
            <button
              onClick={handleMaximize}
              style={{
                width: "25px",
                height: "100%",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "23px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
              className="window-control-button"
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? "⮻" : "◻"}
            </button>
            <button
              onClick={handleClose}
              style={{
                width: "25px",
                height: "100%",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Arial, sans-serif",
                color: "white",
              }}
              className="close-button"
              title="Close"
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
        </div>

        {/* Window Content */}
        <div style={contentStyle}>{children}</div>

        {/* Resize Handles */}
        {!isMaximized &&
          resizeHandles.map(({ direction, cursor, style }) => (
            <div
              key={direction}
              style={{
                position: "absolute",
                cursor,
                ...style,
              }}
              onMouseDown={(e) => handleResizeMouseDown(direction, e)}
            />
          ))}
      </div>
    );
  },
);

export default Window;
